import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Check, Globe2, Loader2, Plus, X } from "lucide-react";
import { API } from "@/lib/api";
import {
  AI_IMAGE_POOL,
  BASIC_OPTIONS,
  COUNTRIES,
  HOT_SPRINGS,
  HOT_SPRING_REGIONS,
  OTHER_AREAS_ACTIVITIES,
  OTHER_AREAS_TOURS,
  REYKJAVIK_ACTIVITIES,
  REYKJAVIK_TOURS,
  SERVICES,
} from "@/data/services";

export const ServiceCard = ({ service, selected, onToggle, index }) => (
  <motion.button
    type="button"
    data-testid={`service-card-${service.id}`}
    onClick={() => onToggle(service.id)}
    aria-pressed={selected}
    className={`group relative overflow-hidden border text-left transition-colors duration-500 ${
      selected
        ? "border-amber-400/70 bg-[#181822] shadow-[0_0_40px_rgba(212,175,55,0.15)]"
        : "border-[#26241e] bg-[#14141a] hover:border-amber-500/40"
    }`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
  >
    <div className="relative h-52 overflow-hidden sm:h-60">
      <img
        src={service.image}
        alt={service.name}
        loading="lazy"
        className={`h-full w-full object-cover transition-all duration-700 group-hover:scale-105 ${
          selected ? "opacity-90" : "opacity-60 group-hover:opacity-80"
        }`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#14141a] via-transparent to-transparent" />
      <span className="absolute left-4 top-4 border border-amber-500/30 bg-black/60 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-200/90 backdrop-blur-sm">
        {service.tag}
      </span>
      <span
        data-testid={`service-toggle-${service.id}`}
        className={`absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full border transition-all duration-300 ${
          selected
            ? "border-amber-400 bg-amber-500 text-black"
            : "border-amber-500/40 bg-black/50 text-amber-200 backdrop-blur-sm group-hover:border-amber-300"
        }`}
      >
        {selected ? <Check className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
      </span>
    </div>
    <div className="p-5 sm:p-6">
      <h3 className={`font-serif text-xl tracking-wide transition-colors duration-300 sm:text-2xl ${selected ? "text-amber-200" : "text-neutral-100"}`}>
        {service.name}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-neutral-400">{service.desc}</p>
      <p className={`mt-4 text-[10px] font-semibold uppercase tracking-[0.3em] transition-colors duration-300 ${selected ? "text-amber-300" : "text-neutral-500 group-hover:text-amber-300/70"}`}>
        {selected ? "On your list" : "Tap to add to your list"}
      </p>
    </div>
  </motion.button>
);

const WORLDWIDE_IDS = ["supercars", "jets", "villas", "islands", "yacht", "worldwide"];
const ICELAND_SERVICES = SERVICES.filter((s) => !WORLDWIDE_IDS.includes(s.id));
const WORLDWIDE_SERVICES = SERVICES.filter((s) => WORLDWIDE_IDS.includes(s.id));

const Divider = ({ title, sub, testId }) => (
  <motion.div
    data-testid={testId}
    className="mt-24 flex items-center gap-6"
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
  >
    <div className="gold-hairline flex-1" />
    <div className="text-center">
      <p className="font-luxe text-lg uppercase tracking-[0.3em] text-neutral-50 sm:text-xl">{title}</p>
      <p className="mt-2 font-serif text-sm italic text-amber-200/70">{sub}</p>
    </div>
    <div className="gold-hairline flex-1" />
  </motion.div>
);

const cacheKey = (country) => `er-local-options-${country.toLowerCase()}`;

const orderList = (list, order) => {
  if (!order.length) return list;
  const byId = Object.fromEntries(list.map((s) => [s.id, s]));
  return [...order.map((id) => byId[id]).filter(Boolean), ...list.filter((s) => !order.includes(s.id))];
};

export const Services = ({ selected, onToggle, country, onCountryChange, localOptions, onLocalOptions }) => {
  const [detailed, setDetailed] = useState(false);
  const [otherAreas, setOtherAreas] = useState(false);
  // which hot-springs picker is open: null | "all" (main catalogue card) |
  // "reykjavik" (capital-area card) | "other" (beyond-the-capital card)
  const [hotSpringsScope, setHotSpringsScope] = useState(null);

  // hot-spring picker cards: id -> scope
  const HS_CARD_SCOPES = { "hotsprings": "all", "x-spas": "reykjavik", "oa-hotsprings": "other" };
  const hsIdsFor = (scope) =>
    HOT_SPRINGS.filter((h) => scope === "all" || (scope === "reykjavik" ? h.area === "reykjavik" : h.area !== "reykjavik")).map((h) => h.id);

  const renderPickCard = (s, i) => {
    const scope = HS_CARD_SCOPES[s.id];
    if (!scope) {
      return <ServiceCard key={s.id} service={s} selected={selected.includes(s.id)} onToggle={onToggle} index={i} />;
    }
    const picks = selected.filter((id) => hsIdsFor(scope).includes(id)).length;
    return (
      <div key={s.id} className="relative">
        <ServiceCard service={s} selected={picks > 0} onToggle={() => setHotSpringsScope(scope)} index={i} />
        {picks > 0 && (
          <span
            data-testid={`hot-springs-count-${scope}`}
            className="pointer-events-none absolute left-3 top-3 z-10 border border-amber-400/60 bg-black/80 px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.2em] text-amber-300"
          >
            {picks} spring{picks === 1 ? "" : "s"} chosen
          </span>
        )}
      </div>
    );
  };
  const [localState, setLocalState] = useState("idle"); // idle | loading | done
  const [order, setOrder] = useState([]);
  const isIceland = country === "Iceland";

  useEffect(() => {
    axios
      .get(`${API}/catalogue/order`)
      .then((res) => setOrder(res.data.order || []))
      .catch(() => {});
  }, []);

  const icelandServices = orderList(ICELAND_SERVICES, order);
  const worldwideServices = orderList(WORLDWIDE_SERVICES, order);

  useEffect(() => {
    if (isIceland) return;
    let cancelled = false;
    const cached = localStorage.getItem(cacheKey(country));
    if (cached) {
      try {
        onLocalOptions(JSON.parse(cached));
        setLocalState("done");
        return;
      } catch {
        /* fall through to fetch */
      }
    }
    setLocalState("loading");
    axios
      .post(`${API}/catalogue/local-options`, { country })
      .then((res) => {
        if (cancelled) return;
        const opts = (res.data.options || []).map((o, i) => ({
          id: `ai-${country.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${i}`,
          name: o.name,
          tag: o.tag || "Signature",
          desc: o.desc,
          image: AI_IMAGE_POOL[i % AI_IMAGE_POOL.length],
        }));
        const known = res.data.known && opts.length > 0;
        const result = { known, options: known ? opts : [] };
        localStorage.setItem(cacheKey(country), JSON.stringify(result));
        onLocalOptions(result);
        setLocalState("done");
      })
      .catch(() => {
        if (cancelled) return;
        onLocalOptions({ known: false, options: [] });
        setLocalState("done");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  const localGrid = (() => {
    if (localState === "loading")
      return (
        <div data-testid="local-options-loading" className="mt-14 flex items-center justify-center gap-3 border border-dashed border-amber-500/25 py-16">
          <Loader2 className="h-5 w-5 animate-spin text-amber-400" />
          <p className="text-xs uppercase tracking-[0.3em] text-neutral-400">Curating {country} for you...</p>
        </div>
      );
    const known = localOptions?.known && localOptions.options?.length > 0;
    const cards = known ? localOptions.options : BASIC_OPTIONS;
    return (
      <>
        {!known && localState === "done" && (
          <p data-testid="local-options-basic-note" className="mt-8 text-center font-serif text-sm italic text-amber-200/70">
            We compose {country} from scratch — start with the essentials below and tell us the rest in your own words.
          </p>
        )}
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {cards.map((s, i) => (
            <ServiceCard key={s.id} service={s} selected={selected.includes(s.id)} onToggle={onToggle} index={i} />
          ))}
        </div>
      </>
    );
  })();

  return (
    <section id="services" data-testid="services-section" className="relative bg-[#0c0c10] py-24 sm:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <motion.p
              className="text-xs font-semibold uppercase tracking-[0.35em] text-amber-300/80"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              The Catalogue
            </motion.p>
            <motion.h2
              className="mt-4 font-luxe text-2xl uppercase leading-[1.2] tracking-[0.12em] text-neutral-50 sm:text-4xl"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              Press anything you desire.
              <br />
              <span className="font-serif normal-case italic text-amber-200">It waits on your list.</span>
            </motion.h2>
            <motion.label
              className="mt-6 flex items-center gap-3"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.15 }}
            >
              <Globe2 className="h-4 w-4 text-amber-400/80" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Your destination</span>
              <select
                data-testid="country-select"
                value={country}
                onChange={(e) => onCountryChange(e.target.value)}
                className="border border-amber-500/40 bg-[#0d0d12] px-4 py-2.5 font-serif text-base text-amber-100 outline-none transition-colors focus:border-amber-300"
              >
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </motion.label>
          </div>
          <motion.p
            data-testid="services-selected-count"
            className="border border-amber-500/30 px-5 py-3 text-xs uppercase tracking-[0.25em] text-amber-200/90"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {selected.length} selected
          </motion.p>
        </div>

        {isIceland ? (
          <>
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {icelandServices.map((s, i) => renderPickCard(s, i))}
            </div>

            <div className="mt-14 text-center">
              <button
                data-testid="more-detailed-request"
                onClick={() => setDetailed((d) => !d)}
                className="border border-amber-500/50 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors duration-300 hover:bg-amber-500 hover:text-black"
              >
                {detailed ? "Fewer options" : "More detailed request"}
              </button>
              {!detailed && (
                <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-neutral-600">
                  Open the full Iceland repertoire — spas, wildlife, icons & more
                </p>
              )}
            </div>

            {detailed && (
              <>
                <Divider title="The Full Iceland Repertoire" sub="Every door on the island, open" testId="extended-iceland-divider" />

                <div data-testid="reykjavik-tours-head" className="mt-12 text-center">
                  <h3 className="font-serif text-3xl font-light tracking-wide text-neutral-50">Tours from Reykjavik</h3>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-amber-300/70">Every tour is private — your guide, your vehicle, your schedule</p>
                </div>
                <div data-testid="reykjavik-tours-grid" className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                  {REYKJAVIK_TOURS.map((s, i) => (
                    <ServiceCard key={s.id} service={s} selected={selected.includes(s.id)} onToggle={onToggle} index={i} />
                  ))}
                </div>

                <div data-testid="reykjavik-activities-head" className="mt-16 text-center">
                  <h3 className="font-serif text-3xl font-light tracking-wide text-neutral-50">Activities from Reykjavik</h3>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-amber-300/70">All depart from the capital</p>
                </div>
                <div data-testid="reykjavik-activities-grid" className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                  {REYKJAVIK_ACTIVITIES.map((s, i) => renderPickCard(s, i))}
                </div>

                <div className="mt-16 text-center">
                  <button
                    data-testid="other-areas-toggle"
                    onClick={() => setOtherAreas((o) => !o)}
                    aria-expanded={otherAreas}
                    className="border border-amber-500/50 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors duration-300 hover:bg-amber-500 hover:text-black"
                  >
                    {otherAreas ? "Close other areas" : "Other areas in Iceland"}
                  </button>
                  {!otherAreas && (
                    <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-neutral-600">
                      The Ring Road, the Westfjords, the North, the East & more
                    </p>
                  )}
                </div>

                {otherAreas && (
                  <>
                    <div data-testid="other-tours-head" className="mt-12 text-center">
                      <h3 className="font-serif text-3xl font-light tracking-wide text-neutral-50">Tours — Beyond the Capital</h3>
                      <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-amber-300/70">Multi-day private journeys across the island</p>
                    </div>
                    <div data-testid="other-tours-grid" className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                      {OTHER_AREAS_TOURS.map((s, i) => (
                        <ServiceCard key={s.id} service={s} selected={selected.includes(s.id)} onToggle={onToggle} index={i} />
                      ))}
                    </div>

                    <div data-testid="other-activities-head" className="mt-16 text-center">
                      <h3 className="font-serif text-3xl font-light tracking-wide text-neutral-50">Activities — Beyond the Capital</h3>
                    </div>
                    <div data-testid="other-activities-grid" className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                      {OTHER_AREAS_ACTIVITIES.map((s, i) => renderPickCard(s, i))}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <Divider title={`Curated for ${country}`} sub="The island steps aside — the world steps in" testId="local-divider" />
            {localGrid}
          </>
        )}

        <Divider
          title="The Worldwide Collection"
          sub="The same key opens doors on every continent"
          testId="worldwide-divider"
        />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {worldwideServices.map((s, i) => (
            <ServiceCard key={s.id} service={s} selected={selected.includes(s.id)} onToggle={onToggle} index={i} />
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link
            to="/budget"
            data-testid="budget-options-button"
            className="inline-flex items-center gap-3 border border-neutral-600 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-neutral-300 transition-colors duration-300 hover:border-amber-400 hover:text-amber-200"
          >
            Budget options <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-neutral-600">
            Four-star hotels, rental cars, RVs & campers — the smart side of Iceland
          </p>
        </div>
      </div>

      {/* Geothermal Hot Springs picker — multi-select, grouped by region */}
      {hotSpringsScope && (() => {
        const scopeIds = hsIdsFor(hotSpringsScope);
        const picks = selected.filter((id) => scopeIds.includes(id)).length;
        const groups = HOT_SPRING_REGIONS.map((r) => ({
          ...r,
          items: HOT_SPRINGS.filter((h) => h.area === r.key && scopeIds.includes(h.id)),
        })).filter((g) => g.items.length > 0);
        const title =
          hotSpringsScope === "reykjavik"
            ? "Hot springs around Reykjavík"
            : hotSpringsScope === "other"
              ? "Hot springs beyond the capital"
              : "Pick as many as you like";
        return (
          <div
            data-testid="hot-springs-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
            onClick={() => setHotSpringsScope(null)}
          >
            <div
              className="max-h-[85vh] w-full max-w-2xl overflow-y-auto border border-amber-500/25 bg-[#0c0c10] p-6 sm:p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Geothermal Hot Springs</p>
                  <h3 className="mt-2 font-serif text-2xl font-light text-neutral-50 sm:text-3xl">{title}</h3>
                  <p className="mt-2 text-xs text-neutral-500">
                    Tap a spring to add it to your list — tap again to remove it.
                    {picks > 0 && (
                      <span data-testid="hot-springs-modal-count" className="ml-2 text-amber-300">{picks} chosen</span>
                    )}
                  </p>
                </div>
                <button
                  data-testid="hot-springs-close"
                  onClick={() => setHotSpringsScope(null)}
                  aria-label="Close"
                  className="p-1 text-neutral-500 transition-colors hover:text-amber-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {groups.map((g) => (
                <div key={g.key}>
                  <p
                    data-testid={`hs-group-${g.key}`}
                    className="mt-6 border-b border-neutral-800 pb-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/70"
                  >
                    {g.label}
                  </p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {g.items.map((h) => {
                      const on = selected.includes(h.id);
                      return (
                        <button
                          key={h.id}
                          data-testid={`hs-option-${h.id}`}
                          onClick={() => onToggle(h.id)}
                          aria-pressed={on}
                          className={`flex items-center justify-between gap-2 border px-3.5 py-2.5 text-left text-sm transition-colors ${
                            on
                              ? "border-amber-400/70 bg-amber-500/15 text-amber-100"
                              : "border-neutral-700/70 text-neutral-300 hover:border-amber-500/40 hover:text-amber-100"
                          }`}
                        >
                          <span>
                            <span className="block font-serif">{h.name}</span>
                            <span className={`mt-0.5 block text-[11px] leading-snug ${on ? "text-amber-200/60" : "text-neutral-500"}`}>{h.blurb}</span>
                          </span>
                          {on && <Check className="h-3.5 w-3.5 shrink-0 text-amber-300" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              <button
                data-testid="hot-springs-done"
                onClick={() => setHotSpringsScope(null)}
                className="mt-7 w-full border border-amber-500/60 px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
              >
                Done{picks > 0 ? ` — ${picks} on your list` : ""}
              </button>
            </div>
          </div>
        );
      })()}
    </section>
  );
};
